// FieldIQ — Profile tab — settings, stats, push prefs

import { useState, useEffect, useRef } from "react";
import {
  LogOut,
  Mail,
  Shield,
  MapPin,
  Upload,
  Play,
  Map,
  Phone,
  Bell,
  CheckCircle2,
  MoreVertical,
  Share,
  PlusSquare,
} from "lucide-react";
import { Avatar } from "../components/ui";
import { authFetch } from "../lib/api";
import { interactionBadge, streakBadge } from "../lib/helpers";
import {
  getIconPreview,
  getInstallState,
  promptInstall,
  subscribeInstallState,
} from "../lib/pwa";

export const ProfileTab = ({
  user,
  facility,
  onLogout,
  onPhotoChange,
  topicPrefs,
  onTopicPrefsChange,
  apiTopics,
  topicsEnabled = true,
  totalInteractions = 0,
  pushPermission = "default",
  pushSubscribed = false,
  pushPrefs = {},
  onEnablePush,
  onDisablePush,
  onPushPrefsChange,
  onReplayTour,
}) => {
  const [photoUrl, setPhotoUrl] = useState(() => {
    try {
      return localStorage.getItem("fieldiq_profile_photo") || null;
    } catch {
      return null;
    }
  });
  const photoInputRef = useRef(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const closePasswordForm = () => {
    setPwOpen(false);
    setPwForm({ current: "", next: "", confirm: "" });
    setPwMsg(null);
  };

  const handlePasswordChange = async () => {
    const { current, next, confirm } = pwForm;
    if (!current || !next) {
      setPwMsg({ err: true, text: "Enter your current and new password." });
      return;
    }
    // Checked here as well as server-side so the mismatch case does not cost a
    // round-trip and a rejected attempt.
    if (next !== confirm) {
      setPwMsg({ err: true, text: "The new passwords do not match." });
      return;
    }
    if (next.length < 8) {
      setPwMsg({ err: true, text: "Use at least 8 characters." });
      return;
    }
    setPwSaving(true);
    setPwMsg(null);
    const res = await authFetch("/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password: current, new_password: next }),
    });
    setPwSaving(false);
    if (res?.error || !res?.success) {
      // authFetch hands back the raw response body, so a Flask error arrives
      // as the JSON string '{"error":"..."}' rather than the message itself.
      let text = res?.error || "Could not update password.";
      try {
        text = JSON.parse(text).error || text;
      } catch {
        /* not JSON — show it as-is */
      }
      setPwMsg({ err: true, text });
      return;
    }
    // Keep the panel open just long enough to show it worked; the fields are
    // cleared immediately so a shared device is not left holding them.
    setPwForm({ current: "", next: "", confirm: "" });
    setPwMsg({ err: false, text: "Password updated." });
  };

  const [precinct, setPrecinct] = useState(user?.precinct || "");
  const [precinctDraft, setPrecinctDraft] = useState(user?.precinct || "");
  const [precinctSaving, setPrecinctSaving] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneDraft, setPhoneDraft] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [ldDistrict, setLdDistrict] = useState(facility?.ld_district || "");

  // Add-to-home-screen. The manifest swap happens in lib/pwa.js; this only
  // mirrors its state so the card can show the right affordance per platform.
  const [installState, setInstallState] = useState(getInstallState);
  const [homeIcon, setHomeIcon] = useState(getIconPreview);
  const [installMsg, setInstallMsg] = useState("");
  const [highlightSteps, setHighlightSteps] = useState(false);
  const highlightTimer = useRef(null);
  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  useEffect(() => {
    const sync = () => {
      setInstallState(getInstallState());
      setHomeIcon(getIconPreview());
    };
    sync();
    return subscribeInstallState(sync);
  }, []);

  const handleInstall = async () => {
    // Safari, and any browser that has not offered a prompt, cannot be driven
    // programmatically — so point at the steps instead of silently doing
    // nothing, which is how the button read on iOS before.
    if (!installState.canPrompt) {
      setInstallMsg(
        installState.ios
          ? `${installState.iosBrowser || "This browser"} adds apps from its Share menu — follow the two steps below.`
          : "This browser hasn't offered a one-tap install — use the steps below.",
      );
      setHighlightSteps(true);
      clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlightSteps(false), 2500);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "dismissed")
      setInstallMsg("Install cancelled — tap again whenever you like.");
  };

  const handlePrecinctSave = async () => {
    setPrecinctSaving(true);
    setPrecinct(precinctDraft);
    await authFetch("/fieldiq/profile", {
      method: "PUT",
      body: JSON.stringify({ precinct: precinctDraft }),
    }).catch(() => {});
    setPrecinctSaving(false);
  };

  const handlePhoneSave = async () => {
    setPhoneSaving(true);
    setPhone(phoneDraft);
    await authFetch("/fieldiq/profile", {
      method: "PUT",
      body: JSON.stringify({ phone: phoneDraft }),
    }).catch(() => {});
    setPhoneSaving(false);
  };

  useEffect(() => {
    authFetch("/fieldiq/profile")
      .then((data) => {
        if (data?.photo_url) {
          setPhotoUrl(data.photo_url);
          onPhotoChange?.(data.photo_url);
          try {
            localStorage.setItem("fieldiq_profile_photo", data.photo_url);
          } catch {}
        }
        if (data?.precinct != null) {
          setPrecinct(String(data.precinct));
          setPrecinctDraft(String(data.precinct));
        }
        if (data?.phone != null) {
          setPhone(String(data.phone));
          setPhoneDraft(String(data.phone));
        }
        if (data?.ld_district) {
          setLdDistrict(String(data.ld_district));
        }
      })
      .catch(() => {});
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Photo must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const SIZE = 256;
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");
        const minSide = Math.min(img.width, img.height);
        const sx = (img.width - minSide) / 2;
        const sy = (img.height - minSide) / 2;
        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, SIZE, SIZE);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        setPhotoUrl(dataUrl);
        onPhotoChange?.(dataUrl);
        try {
          localStorage.setItem("fieldiq_profile_photo", dataUrl);
        } catch {}
        authFetch("/fieldiq/profile", {
          method: "PUT",
          body: JSON.stringify({ photo_url: dataUrl }),
        }).catch(() => {});
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const displayName =
    user?.name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.email ||
    "My Profile";

  // FieldIQ is multi-vertical: District, Precinct and constituent-interaction
  // Awards are political concepts and must not show for a construction facility.
  // Default to political when vertical_id is absent (legacy / offline cache).
  const isPolitical = (facility?.vertical_id || "political") === "political";

  const fields = [
    { icon: Mail, label: "Email", value: user?.email || "—" },
    { icon: Shield, label: "Role", value: user?.role || "—" },
    ...(isPolitical
      ? [{ icon: MapPin, label: "District", value: ldDistrict || "—" }]
      : []),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center pt-4 pb-2">
        <button
          onClick={() => photoInputRef.current?.click()}
          className="relative group"
          title="Change photo"
        >
          <Avatar
            name={displayName}
            photoUrl={photoUrl}
            size={20}
            colorClass="from-cyan-500 to-blue-600"
          />
          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 group-active:opacity-100 flex items-center justify-center transition-opacity">
            <Upload size={20} className="text-white" />
          </div>
        </button>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handlePhotoChange}
        />
        <p className="text-xs text-slate-600 mt-2">Tap to change photo</p>
        <div className="text-lg font-semibold text-white mt-1">
          {displayName}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">FieldIQ Member</div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl divide-y divide-slate-700">
        {fields.map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-3 px-4 py-3">
            <Icon size={16} className="text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-400 w-16 flex-shrink-0">
              {label}
            </span>
            <span className="text-sm text-white">{value}</span>
          </div>
        ))}
        {isPolitical && (
          <div className="flex items-center gap-3 px-4 py-3">
            <Map size={16} className="text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-400 w-16 flex-shrink-0">
              Precinct
            </span>
            <input
              type="text"
              value={precinctDraft}
              onChange={(e) => setPrecinctDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePrecinctSave()}
              placeholder="e.g. 42"
              className="flex-1 bg-transparent text-sm text-white outline-none placeholder-slate-600"
            />
            {precinctDraft !== precinct && (
              <button
                onClick={handlePrecinctSave}
                disabled={precinctSaving}
                className="text-xs text-sky-400 hover:text-sky-300 disabled:opacity-50 font-medium"
              >
                {precinctSaving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 px-4 py-3">
          <Phone size={16} className="text-slate-400 flex-shrink-0" />
          <span className="text-xs text-slate-400 w-16 flex-shrink-0">
            Phone
          </span>
          <input
            type="tel"
            value={phoneDraft}
            onChange={(e) => setPhoneDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePhoneSave()}
            placeholder="e.g. 602-555-0100"
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder-slate-600"
          />
          {phoneDraft !== phone && (
            <button
              onClick={handlePhoneSave}
              disabled={phoneSaving}
              className="text-xs text-sky-400 hover:text-sky-300 disabled:opacity-50 font-medium"
            >
              {phoneSaving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      {/* Streak / Awards */}
      {(() => {
        // Facility-aware login streak: prefer the active facility's streak once
        // the backend supplies it on /my-services (per-facility usage tracking),
        // falling back to the user-global streak until then. Awards are
        // login-only for now; construction metrics evolve as those modules land.
        const streak = facility?.streak_days ?? user?.streak_days ?? 1;
        const { emojis, label } = streakBadge(streak);
        // Constituent-interaction awards are political-only (and the count is
        // still user-global today — see the per-facility follow-up).
        const iBadge = isPolitical ? interactionBadge(totalInteractions) : null;
        const milestones = [];
        if (streak >= 25) milestones.push({ e: "🏆", text: "25-day legend" });
        if (streak >= 20)
          milestones.push({ e: "🔥🔥🔥🔥🔥", text: "20-day inferno" });
        if (streak >= 15)
          milestones.push({ e: "🔥🔥🔥🔥", text: "15-day streak" });
        if (streak >= 10)
          milestones.push({ e: "🔥🔥🔥", text: "10-day streak" });
        if (streak >= 5) milestones.push({ e: "🔥🔥", text: "5-day streak" });
        milestones.push({ e: "🔥", text: "First login" });
        if (isPolitical && totalInteractions >= 5)
          milestones.push({ e: "🏆", text: "5 interactions" });
        else if (isPolitical && totalInteractions >= 1)
          milestones.push({
            e: "🎯".repeat(totalInteractions),
            text: `${totalInteractions} interaction${totalInteractions !== 1 ? "s" : ""}`,
          });
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                Awards
              </p>
              <span className="text-xs text-slate-500">{label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-3xl leading-none">{emojis}</span>
              <div>
                <p className="text-sm font-semibold text-white">
                  {streak >= 25
                    ? "Legendary streak"
                    : streak >= 20
                      ? "Inferno streak"
                      : streak >= 15
                        ? "4-fire streak"
                        : streak >= 10
                          ? "3-fire streak"
                          : streak >= 5
                            ? "2-fire streak"
                            : "On a roll"}
                </p>
                <p className="text-xs text-slate-400">
                  {streak} consecutive login{streak !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
            {iBadge && (
              <div className="flex items-center gap-3 pt-3 border-t border-slate-700">
                <span className="text-3xl leading-none">{iBadge.emojis}</span>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {totalInteractions >= 5
                      ? "Field legend"
                      : "Getting out there"}
                  </p>
                  <p className="text-xs text-slate-400">
                    {iBadge.label} logged
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-700">
              {milestones.map((m) => (
                <span
                  key={m.text}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/60 border border-slate-600/50 rounded-full text-xs text-slate-300"
                >
                  <span>{m.e}</span>
                  <span>{m.text}</span>
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Your Topics picker — the copy below promises these "appear on your
          Today tab", which is only true where Topics is in the nav */}
      {topicsEnabled &&
        (() => {
          const allTopics = apiTopics;
          const selectedIds =
            topicPrefs || allTopics.slice(0, 3).map((t) => t.slug || t.id);
          const toggle = (slug) => {
            if (selectedIds.includes(slug)) {
              const next = selectedIds.filter((x) => x !== slug);
              onTopicPrefsChange?.(next.length ? next : selectedIds);
            } else if (selectedIds.length < 3) {
              onTopicPrefsChange?.([...selectedIds, slug]);
            }
          };
          return (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Your Topics
                </p>
                <span className="text-xs text-slate-500">
                  {selectedIds.length}/3 selected
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allTopics.map((topic) => {
                  const tSlug = topic.slug || topic.id;
                  const on = selectedIds.includes(tSlug);
                  const atMax = selectedIds.length >= 3 && !on;
                  return (
                    <button
                      key={tSlug}
                      onClick={() => toggle(tSlug)}
                      disabled={atMax}
                      className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold border transition-colors ${
                        on
                          ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-300"
                          : atMax
                            ? "bg-slate-800 border-slate-700/50 text-slate-600 cursor-not-allowed"
                            : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300"
                      }`}
                    >
                      {topic.hot && (
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                      )}
                      {topic.label}
                      {on && (
                        <span className="ml-0.5 text-cyan-400 leading-none">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-600 mt-2">
                These appear on your Today tab.
              </p>
            </div>
          );
        })()}

      {/* Push Notifications */}
      {(() => {
        const PUSH_CATEGORIES = [
          { key: "events", label: "Events & Reminders" },
          { key: "walks", label: "Walk Assignments" },
          { key: "broadcasts", label: "Leadership Broadcasts" },
          { key: "teams", label: "Team Messages" },
          { key: "kb", label: "KB Updates" },
        ];
        const supported =
          "Notification" in window && "serviceWorker" in navigator;
        // iOS delivers Web Push only to home-screen web apps, never to a
        // browser tab — in a tab, window.Notification does not even exist. So
        // on iPhone this is not "unsupported", it is "not installed yet".
        const needsInstall =
          !supported && installState.ios && !installState.standalone;
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={15} className="text-slate-400" />
                <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
                  Notifications
                </p>
              </div>
              {!supported ? (
                <span className="text-xs text-slate-500">
                  {needsInstall ? "Needs install" : "Not supported"}
                </span>
              ) : pushPermission === "denied" ? (
                <span className="px-2 py-0.5 rounded-full text-xs bg-rose-900/50 text-rose-400">
                  Blocked in browser
                </span>
              ) : pushSubscribed ? (
                <button
                  onClick={onDisablePush}
                  className="text-xs text-slate-500 hover:text-rose-400 transition-colors"
                >
                  Disable
                </button>
              ) : (
                <button
                  onClick={onEnablePush}
                  className="px-3 py-1 text-xs font-medium bg-cyan-600 hover:bg-cyan-700 text-white rounded-full transition-colors"
                >
                  Enable
                </button>
              )}
            </div>

            {pushSubscribed && (
              <div className="space-y-2 pt-1">
                {PUSH_CATEGORIES.map(({ key, label }) => (
                  <label
                    key={key}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="text-xs text-slate-400">{label}</span>
                    <button
                      role="switch"
                      aria-checked={!!pushPrefs[key]}
                      onClick={() =>
                        onPushPrefsChange?.({
                          ...pushPrefs,
                          [key]: !pushPrefs[key],
                        })
                      }
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        pushPrefs[key] ? "bg-cyan-600" : "bg-slate-600"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          pushPrefs[key] ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>
                ))}
              </div>
            )}

            {needsInstall && (
              <p className="text-xs text-slate-400">
                Add FieldIQ to your Home Screen using the card above, then open
                it from the icon — iPhone only delivers notifications to
                installed apps.
              </p>
            )}

            {!pushSubscribed && supported && pushPermission !== "denied" && (
              <p className="text-xs text-slate-500">
                Get notified about walk assignments, events, and leadership
                updates — even when the app is closed.
              </p>
            )}
          </div>
        );
      })()}

      {/* Add to Home Screen */}
      {(() => {
        const { standalone, installed, canPrompt, ios, iosBrowser } =
          installState;
        // No iOS browser fires an install prompt — they are all WebKit — so on
        // iOS the steps are the only route, and they must name the browser the
        // user is actually holding. Everywhere else the steps are the manual
        // fallback for when the prompt was dismissed or never became available.
        const installSteps = ios
          ? [
              {
                icon: Share,
                text: `Tap the Share button in ${iosBrowser || "your browser"}`,
              },
              {
                icon: PlusSquare,
                text: 'Choose "Add to Home Screen", then Add',
              },
            ]
          : [
              { icon: MoreVertical, text: "Open the browser menu (⋮ or ⋯)" },
              {
                icon: PlusSquare,
                text: 'Choose "Install app" or "Add to Home Screen"',
              },
            ];
        const iconSrc =
          homeIcon ||
          facility?.logo_url ||
          facility?.services?.find((s) => s.service_type === "rag")?.config
            ?.logo_url ||
          "/logo-white.png";
        const facilityName = facility?.facility_name || "FieldIQ";

        return (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <PlusSquare size={15} className="text-slate-400" />
              <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
                Home Screen
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Preview of the icon that actually lands on the home screen */}
              <div className="w-14 h-14 flex-shrink-0 rounded-[13px] bg-slate-950 border border-slate-700 overflow-hidden flex items-center justify-center">
                <img
                  src={iconSrc}
                  alt={`${facilityName} icon`}
                  className="w-full h-full object-contain p-1.5"
                  onError={(e) => {
                    e.currentTarget.src = "/logo-white.png";
                  }}
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {facilityName}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {standalone || installed
                    ? "Installed on this device"
                    : ios
                      ? "Required for notifications on iPhone"
                      : "Install FieldIQ with your organization's logo"}
                </p>
              </div>
            </div>

            {standalone || installed ? (
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <CheckCircle2 size={14} className="flex-shrink-0" />
                You're running FieldIQ from your home screen.
              </div>
            ) : (
              <>
                {/* The button is always here. Where the browser gives us a real
                    install prompt it fires it; on Safari — which never will —
                    it points at the steps rather than doing nothing. */}
                <button
                  onClick={handleInstall}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-sm font-semibold text-white transition-colors"
                >
                  <PlusSquare size={15} />
                  Add to Home Screen
                </button>

                <div
                  className={`rounded-lg border p-3 transition-colors ${
                    highlightSteps
                      ? "border-cyan-500/70 bg-cyan-500/5"
                      : "border-slate-700/70 bg-slate-900/40"
                  }`}
                >
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                    {canPrompt ? "Or do it manually" : "How to add it"}
                  </p>
                  <ol className="space-y-1.5 text-xs text-slate-400">
                    {installSteps.map(({ icon: StepIcon, text }, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <StepIcon
                          size={13}
                          className="text-cyan-400 flex-shrink-0"
                        />
                        {text}
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}

            {installMsg && (
              <p className="text-xs text-slate-500">{installMsg}</p>
            )}
          </div>
        );
      })()}

      {/* Change password. Uses /auth/change-password, which requires the
          current one — so this is safe on a shared or unattended device, and
          it is the way to replace a temporary password an admin read out. */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Password
          </p>
          {!pwOpen && (
            <button
              onClick={() => setPwOpen(true)}
              className="text-xs text-sky-400 hover:text-sky-300 font-medium"
            >
              Change
            </button>
          )}
        </div>

        {!pwOpen ? (
          <p className="text-xs text-slate-500">
            Set a new password for this account.
          </p>
        ) : (
          <div className="space-y-2">
            {[
              ["current", "Current password", "current-password"],
              ["next", "New password", "new-password"],
              ["confirm", "Confirm new password", "new-password"],
            ].map(([key, label, autoComplete]) => (
              <input
                key={key}
                type="password"
                autoComplete={autoComplete}
                value={pwForm[key]}
                onChange={(e) =>
                  setPwForm((f) => ({ ...f, [key]: e.target.value }))
                }
                placeholder={label}
                aria-label={label}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none placeholder-slate-600 focus:border-sky-500"
              />
            ))}

            {pwMsg && (
              <p
                className={`text-xs ${pwMsg.err ? "text-red-400" : "text-emerald-400"}`}
              >
                {pwMsg.text}
              </p>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handlePasswordChange}
                disabled={pwSaving}
                className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 rounded-lg text-sm text-white font-medium"
              >
                {pwSaving ? "Saving…" : "Update password"}
              </button>
              <button
                onClick={closePasswordForm}
                className="text-xs text-slate-400 hover:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={onReplayTour}
        className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-300 hover:bg-slate-700 transition-colors"
      >
        <Play size={16} />
        Watch Intro Tour
      </button>

      <button
        onClick={onLogout}
        className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800 border border-slate-700 rounded-xl text-sm text-red-400 hover:bg-slate-700 transition-colors"
      >
        <LogOut size={16} />
        Sign Out
      </button>
    </div>
  );
};
